import { Injectable } from '@angular/core';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * The editor's toolbar, expressed as HTML.
 *
 * This list is not a style choice — it is the same vocabulary the backend
 * enforces on the way in (`ContentFormatConverter::restrictToEditorVocabulary()`
 * and `cleanWordPressHtml()`). The editor is built with BlockQuote, Bold,
 * Heading, Indent, Italic, Link, List and Paragraph, and its Heading plugin
 * offers h2/h3/h4 — so anything else is markup nobody can author or edit.
 *
 * Keep the two lists in step. If a plugin is added to the CKEditor build, this
 * is one of the places that has to learn about it.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'h2',
  'h3',
  'h4',
  'strong',
  'em',
  'a',
  'blockquote',
  'ul',
  'ol',
  'li',
];

/**
 * `href` is the only attribute any of the above carries in *stored content*.
 * `target` and `rel` are here because the hook below adds them after
 * sanitisation, and DOMPurify strips anything not on this list — including its
 * own hook's output.
 */
const ALLOWED_ATTR = ['href', 'target', 'rel'];

/**
 * Byte-for-byte the scheme check in `ContentFormatConverter::applyAttributeAllowList()`.
 * DOMPurify's own default would also reject `javascript:`; this is here so the
 * two repositories agree on one answer rather than two that happen to overlap.
 */
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|\/|#)/i;

/**
 * Renders stored insight body Markdown to HTML that is safe to put in the DOM.
 *
 * **Why this exists.** Insight bodies are stored as canonical GFM in CKEditor's
 * dialect (see the backend's `ContentFormatConverter`). Something has to turn
 * that into HTML at render time, and until this service existed the card pushed
 * the raw Markdown through `[innerHTML]`, so readers saw literal `*   ` bullets
 * and `_underscores_`.
 *
 * **Why `marked`.** It is the same renderer `@ckeditor/ckeditor5-markdown-gfm`
 * uses, and it was already in the tree as that package's transitive dependency.
 * Rendering with anything else risks disagreeing with the editor about what a
 * document means, which shows up as content that changes appearance the first
 * time someone saves it.
 *
 * **Why sanitise at all, when the backend already does and Angular does too.**
 * Three layers, each closing something the others do not:
 *   - the backend cleans what contributors write, but not what a compromised or
 *     future API response might contain;
 *   - Angular's `[innerHTML]` sanitiser blocks script execution, but happily
 *     renders `<img>` and `<table>` — markup outside the editor's vocabulary;
 *   - this restricts the output to what the editor can actually produce.
 *
 * Full background, including every trap and how to verify a change:
 * `libs/ckeditor/insight-markdown-editor-alignment.md`, in this repository.
 *
 * The result is returned as a plain string, deliberately **not** `SafeHtml`.
 * Binding it through `[innerHTML]` keeps Angular's own sanitiser in the path;
 * calling `bypassSecurityTrustHtml` would switch that layer off.
 */
@Injectable({ providedIn: 'root' })
export class InsightMarkdownService {
  private hookInstalled = false;

  /**
   * A `marked` tokenizer with autolinking switched off.
   *
   * **Why:** only links somebody deliberately made should be active. A URL or
   * email address typed as prose stays prose. `autolink` handles `<https://x>`,
   * `url` handles bare `https://x` and `www.x` — both are stubbed out, exactly
   * as `ckeditor5-markdown-gfm` does, so the reader and the editor agree about
   * what is a link. The backend enforces the same rule on the way in, by not
   * registering CommonMark's `AutolinkExtension`.
   *
   * **Why an instance rather than `marked.use()`:** CKEditor applies these
   * overrides by mutating the shared `marked` module. That works, but it is
   * global — anything else in the bundle importing `marked` would silently
   * inherit them, and the mutation order would decide the outcome. Passing a
   * tokenizer through the options keeps it scoped to this renderer. `Lexer`
   * honours `options.tokenizer` (marked 4: `this.options.tokenizer || new
   * Tokenizer()`), so the effect is identical without the shared state.
   *
   * Returning `undefined` is how a marked tokenizer says "no match here", which
   * lets the text fall through to the ordinary inline rules.
   */
  private readonly tokenizer = (() => {
    // `marked` ships no type definitions and @types/marked is not installed, so
    // this is `any` regardless; the cast just makes that explicit.
    const instance = new (marked as any).Tokenizer();
    instance.autolink = () => undefined;
    instance.url = () => undefined;

    return instance;
  })();

  render(markdown: string | null | undefined): string {
    const source = (markdown ?? '').trim();

    if (!source) {
      return '';
    }

    this.installExternalLinkHook();

    // These options mirror ckeditor5-markdown-gfm's markdown2html.js exactly,
    // read from the deployed bundle (CKEditor 41.1.0) rather than from
    // node_modules. Verified 2026-08-08 — see libs/ckeditor/README.md.
    const html = marked.parse(source, {
      gfm: true,
      // The editor uses `breaks: true`, so a single newline is a line break —
      // the conventional Shift+Enter behaviour. The backend stores a hard break
      // as two trailing spaces, which is a break under either setting; this
      // matters for the bare-newline case, where `false` would silently swallow
      // a break the author made and the editor shows.
      breaks: true,
      tables: true,
      xhtml: true,
      // Heading anchors inject `id` attributes the editor knows nothing about.
      headerIds: false,
      tokenizer: this.tokenizer,
    } as any) as string;

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
      // Text inside a rejected element is kept. A stray <h1> loses its heading
      // level but never its words — losing a reader's sentence to a tag we
      // dislike is the worse failure.
      KEEP_CONTENT: true,
    });
  }

  /**
   * Open external links in a new tab, without handing them a window reference.
   *
   * `rel="noopener noreferrer"` is the part that matters: these hrefs come from
   * whoever wrote the insight, and without it the opened page can navigate the
   * tab it came from. Registered once, and only for absolute http(s) links, so
   * in-app relative links and anchors keep normal navigation.
   */
  private installExternalLinkHook(): void {
    if (this.hookInstalled) {
      return;
    }

    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (!(node instanceof HTMLAnchorElement)) {
        return;
      }

      if (/^https?:/i.test(node.getAttribute('href') ?? '')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });

    this.hookInstalled = true;
  }
}
